import { z } from 'zod';
import {
    CardPointeLifecyclePreflightGatewayOperation,
    CardPointeLifecyclePreflightOutcome,
    CardPointeLifecyclePreflightPayloadType,
    type CardPointeLifecyclePreflightNormalizedResult,
    type CardPointeLifecyclePreflightLegacyRequestPayload,
    type CardPointeLifecyclePreflightPayload,
    type CardPointeLifecyclePreflightRequestDetails,
    type CardPointeLifecyclePreflightRequestPayload,
    type CardPointeLifecyclePreflightResponseDetails,
    type CardPointeLifecyclePreflightResponsePayload,
    type StoredCardPointeLifecyclePreflightRequestPayload,
} from '../../../types/ipg/cardpointe/cardpointe-lifecycle-preflight.types';
import {
    CardPointeTerminalSaleLegacyRequestDetailsSchema,
    CardPointeTerminalSaleRequestDetailsSchema,
} from './cardpointe-terminal-sale.schemas';

export const CardPointeLifecyclePreflightRequestDetailsSchema: z.ZodType<CardPointeLifecyclePreflightRequestDetails> =
    z.object({
        gateway_operation: z.literal(CardPointeLifecyclePreflightGatewayOperation.TERMINAL_SALE),
        terminal_sale_request_details: CardPointeTerminalSaleRequestDetailsSchema,
    });

export const CardPointeLifecyclePreflightNormalizedResultSchema: z.ZodType<CardPointeLifecyclePreflightNormalizedResult> =
    z.object({
        gateway_operation: z.literal(CardPointeLifecyclePreflightGatewayOperation.TERMINAL_SALE),
        merchant_id: z.string().min(1),
        terminal_hsn: z.string().min(1),
        amount: z.string().regex(/^\d+(?:\.\d{2})?$/),
        capture: z.literal('true'),
        order_id: z.string().min(1).optional(),
    });

export const CardPointeLifecyclePreflightRecordedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeLifecyclePreflightOutcome.RECORDED>;
    normalized_result: typeof CardPointeLifecyclePreflightNormalizedResultSchema;
}> = z.object({
    outcome: z.literal(CardPointeLifecyclePreflightOutcome.RECORDED),
    normalized_result: CardPointeLifecyclePreflightNormalizedResultSchema,
}) satisfies z.ZodType<
    Extract<
        CardPointeLifecyclePreflightResponseDetails,
        {
            readonly outcome: typeof CardPointeLifecyclePreflightOutcome.RECORDED;
        }
    >
>;

export const CardPointeLifecyclePreflightRejectedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeLifecyclePreflightOutcome.REJECTED>;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CardPointeLifecyclePreflightOutcome.REJECTED),
    message: z.string().min(1),
}) satisfies z.ZodType<
    Extract<
        CardPointeLifecyclePreflightResponseDetails,
        {
            readonly outcome: typeof CardPointeLifecyclePreflightOutcome.REJECTED;
        }
    >
>;

export const CardPointeLifecyclePreflightMalformedInputResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeLifecyclePreflightOutcome.MALFORMED_INPUT>;
    message: z.ZodString;
    body: z.ZodUnknown;
}> = z.object({
    outcome: z.literal(CardPointeLifecyclePreflightOutcome.MALFORMED_INPUT),
    message: z.string().min(1),
    body: z.unknown(),
}) satisfies z.ZodType<
    Extract<
        CardPointeLifecyclePreflightResponseDetails,
        {
            readonly outcome: typeof CardPointeLifecyclePreflightOutcome.MALFORMED_INPUT;
        }
    >
>;

export const CardPointeLifecyclePreflightResponseDetailsSchema: z.ZodType<CardPointeLifecyclePreflightResponseDetails> =
    z.discriminatedUnion('outcome', [
        CardPointeLifecyclePreflightRecordedResponseDetailsSchema,
        CardPointeLifecyclePreflightRejectedResponseDetailsSchema,
        CardPointeLifecyclePreflightMalformedInputResponseDetailsSchema,
    ]);

export const CardPointeLifecyclePreflightRequestPayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CardPointeLifecyclePreflightPayloadType.REQUEST
    >;
    request_details: typeof CardPointeLifecyclePreflightRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(CardPointeLifecyclePreflightPayloadType.REQUEST),
    request_details: CardPointeLifecyclePreflightRequestDetailsSchema,
}) satisfies z.ZodType<CardPointeLifecyclePreflightRequestPayload>;

export const CardPointeLifecyclePreflightLegacyRequestPayloadSchema: z.ZodType<CardPointeLifecyclePreflightLegacyRequestPayload> =
    z.object({
        gateway_lifecycle_payload_type: z.literal(CardPointeLifecyclePreflightPayloadType.REQUEST),
        request_details: z.object({
            gateway_operation: z.literal(
                CardPointeLifecyclePreflightGatewayOperation.TERMINAL_SALE
            ),
            terminal_sale_request_details: CardPointeTerminalSaleLegacyRequestDetailsSchema,
        }),
    });

export const StoredCardPointeLifecyclePreflightRequestPayloadSchema: z.ZodType<StoredCardPointeLifecyclePreflightRequestPayload> =
    z.union([
        CardPointeLifecyclePreflightRequestPayloadSchema,
        CardPointeLifecyclePreflightLegacyRequestPayloadSchema,
    ]);

export const CardPointeLifecyclePreflightResponsePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CardPointeLifecyclePreflightPayloadType.RESPONSE
    >;
    response_details: typeof CardPointeLifecyclePreflightResponseDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(CardPointeLifecyclePreflightPayloadType.RESPONSE),
    response_details: CardPointeLifecyclePreflightResponseDetailsSchema,
}) satisfies z.ZodType<CardPointeLifecyclePreflightResponsePayload>;

export const CardPointeLifecyclePreflightPayloadSchema: z.ZodType<CardPointeLifecyclePreflightPayload> =
    z.discriminatedUnion('gateway_lifecycle_payload_type', [
        CardPointeLifecyclePreflightRequestPayloadSchema,
        CardPointeLifecyclePreflightResponsePayloadSchema,
    ]);
